/**
 * Rol van een tekst-segment in een planeet-invloed-string.
 *  - `plain`     : neutrale tekst (geen marker eraan vast).
 *  - `personality`: het woord dat direct vóór `(p)` stond.
 *  - `design`    : het woord dat direct vóór `(d)` stond.
 */
export type HdPlanetInfluenceMarkerRole = 'plain' | 'personality' | 'design';

/** Gemarkeerd tekst-segment uit een planeet-invloed-string. */
export interface HdPlanetInfluenceSegment {
  /** Zichtbare tekst van het segment (geen marker). */
  readonly text: string;
  /** Rol van het segment — drijft kleur + superscript-marker aan in de UI. */
  readonly role: HdPlanetInfluenceMarkerRole;
  /**
   * Wanneer aanwezig: de letter die als superscript-marker getoond wordt
   * (`p` of `d`). `null` voor `plain`-segmenten.
   */
  readonly markerSuperscript: 'p' | 'd' | null;
}

/**
 * Parst een planeet-invloed-string en breekt deze op in segmenten.
 *
 * Bron-format: woorden worden voorzien van een marker `(p)` (personality) of
 * `(d)` (design) direct erachter. Voorbeeld:
 *
 *   `"self, main concerns, vitality, ..., lifework(p), brand(p), radiance(d), ..."`
 *
 * Het woord direct voor de marker hoort bij de marker en moet in de UI
 * gekleurd worden. De rest blijft `plain`.
 *
 * Algoritme:
 *  - splits de tekst op marker-tokens via een regex die het meest-rechtse
 *    woord-fragment vlak voor `(p)`/`(d)` vangt;
 *  - alle andere tekst wordt als `plain` doorgegeven.
 *
 * Pure utility, geen side-effects.
 */
export function parsePlanetInfluenceMarkers(text: string): readonly HdPlanetInfluenceSegment[] {
  if (!text) {
    return [];
  }

  const segments: HdPlanetInfluenceSegment[] = [];
  // Vangt het laatste alfanumerieke woord (eventueel met `'`/`-`) dat direct
  // gevolgd wordt door `(p)` of `(d)`. Whitespace mag tussen woord en marker zitten.
  const markerRegex = /([A-Za-z\u00C0-\u017F'’-]+)\s*\(([pd])\)/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = markerRegex.exec(text)) !== null) {
    const wordStart = match.index;
    const wordEnd = wordStart + match[1].length;
    const markerEnd = match.index + match[0].length;

    if (wordStart > lastIndex) {
      segments.push({ text: text.slice(lastIndex, wordStart), role: 'plain', markerSuperscript: null });
    }

    const role: HdPlanetInfluenceMarkerRole = match[2] === 'p' ? 'personality' : 'design';
    segments.push({
      text: text.slice(wordStart, wordEnd),
      role,
      markerSuperscript: match[2] as 'p' | 'd',
    });

    lastIndex = markerEnd;
  }

  if (lastIndex < text.length) {
    segments.push({ text: text.slice(lastIndex), role: 'plain', markerSuperscript: null });
  }

  return segments;
}
